import dayjs from 'dayjs';
import {
    AgencyAlertKind,
    clientDisplayName,
    describeUnreadable,
    IAgencyAlert,
    IAgencyPriorities,
    IClientBaseline,
    IPriorityClient,
    IHistoryProgress,
    IOutcomeMetric
} from '@innerhive-internal/innerhive-common';

/** One client worth watching, newest finding first, with why in a phrase. */
export interface IWorthWatchingRow {
    careMapId: string;
    clientName: string;
    /** Why they are worth watching, in their findings' own words, or a fixed line for a client with no finding in scope. */
    why: string;
    /** When the newest finding was observed, as YYYY-MM-DD; empty for a client with no finding in scope. */
    newestOn: string;
    hasIncident: boolean;
}

/** One client the worklist leaves out, with how they are doing in a line. */
export interface IRestOfCaseloadRow {
    careMapId: string;
    clientName: string;
    note: string;
}

/** How many findings a why names before it counts the rest. */
const WHY_FINDINGS = 2;

/** What leads a why: something that happened, then a change against the baseline, then the rest. */
const KIND_RANK: { [key in AgencyAlertKind]: number } = { incident: 0, baseline: 1, observation: 2 };

const stripFullStop = (sentence: string): string => sentence.trim().replace(/\.$/, '');

const listOf = (items: string[]): string => {
    if (items.length <= 1) {
        return items.join('');
    }
    if (items.length === 2) {
        return `${items[0]} and ${items[1]}`;
    }
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

/**
 * The sentences and worklists the Insights page writes itself, from the figures the digest already
 * holds: who is worth watching and why, and how the rest of the
 * caseload is doing.
 *
 * The same rule as every other sentence on the page: these describe what is on the record, name the
 * people it is about, and never say why anything happened.
 */
export class InsightsNarration {
    static inject = () => [];

    /** "Dorothy M.": enough to place a person in a sentence without printing their whole name. */
    shortName = (clientName: string): string => {
        const parts = clientName.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) {
            return clientDisplayName(clientName);
        }
        if (parts.length === 1) {
            return parts[0];
        }
        return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
    };

    /** Why a client is a priority, as a sentence: the incidents in the recent weeks, the newest of them, and whether more is raised than usual. */
    describePriorityWhy = (client: IPriorityClient): string => {
        const raised = client.raisedAboveUsual ? ' More has been raised about them than in their own earlier weeks.' : '';
        if (!client.latestIncident) {
            return 'More raised about them recently than in their own earlier weeks, with no incident recorded.';
        }
        const count = client.incidentCount === 1 ? 'An incident was recorded' : `${client.incidentCount} incidents were recorded`;
        const when = dayjs(client.latestIncident.observedOn).format('MMM D');
        return `${count} in the recent weeks, the latest on ${when}: ${stripFullStop(client.latestIncident.title)}.${raised}`;
    };

    /**
     * Bea's line under the Needs Attention report: who she would deal with first and why, by first
     * name and last initial, with the incidents the list left out counted.
     */
    describeNeedsAttention = (params: { priorities: IAgencyPriorities; unreadableCount: number }): string => {
        const { priorities, unreadableCount } = params;
        const { listed, unlistedIncidents } = priorities;
        const unread = describeUnreadable(unreadableCount, listed.length > 0);

        if (listed.length === 0) {
            return `Nobody I can read needs dealing with first: no incident in the recent weeks, and nobody above their own baseline.${unread}`;
        }

        const red = listed.filter(client => client.reading === 'incident').map(client => this.shortName(client.clientName));
        const yellow = listed.filter(client => client.reading === 'worthWatching').map(client => this.shortName(client.clientName));
        const sentences: string[] = [];
        if (red.length > 0) {
            const more = unlistedIncidents > 0 ? ` and ${unlistedIncidents} more I have not listed` : '';
            sentences.push(`I would start with ${listOf(red)}${more}: something happened to ${red.length + unlistedIncidents === 1 ? 'them' : 'each of them'} in the recent weeks and it needs following up.`);
        }
        if (yellow.length > 0) {
            const lead = red.length > 0 ? 'Then' : 'I would start with';
            sentences.push(`${lead} ${listOf(yellow)}, with more raised about ${yellow.length === 1 ? 'them' : 'each of them'} than in their own earlier weeks.`);
        }
        return `${sentences.join(' ')}${unread}`;
    };

    /** The outcome figures the caseload's history can support yet, and the ones still waiting on it. */
    splitOutcomes = (params: { outcomes: IOutcomeMetric[]; history: IHistoryProgress }): { shown: IOutcomeMetric[]; waiting: IOutcomeMetric[] } => {
        const { outcomes, history } = params;
        const shown = outcomes.filter(metric => history.hasEnough || !metric.definition.needsHistory);
        return { shown, waiting: outcomes.filter(metric => !shown.includes(metric)) };
    };

    /**
     * One row per client worth watching: the clients the map draws in that reading and the colour
     * key counts, so the three never disagree on who, and within the clients in scope when the page is
     * narrowed. A client something happened to is drawn red and listed at the top of the Needs
     * Attention report instead, so they are not here.
     * Newest finding first, each saying why in the findings' own words: something that happened
     * before a change against their baseline before anything else, so the why leads with the thing
     * a reader would go for. A client with three things raised is one row, not three, and a client
     * worth watching with no finding in scope is still a row, since the map and the colour key already
     * count them.
     */
    worthWatching = (params: {
        baselines: IClientBaseline[];
        /** The findings in scope, of every kind: a client can be worth watching on trends alone. */
        alerts: IAgencyAlert[];
        matchingIds?: string[];
        includeQuiet: boolean;
    }): IWorthWatchingRow[] => {
        const { baselines, alerts, matchingIds, includeQuiet } = params;
        const inScope = matchingIds ? new Set(matchingIds) : undefined;
        const clients = baselines.filter(baseline => baseline.reading === 'worthWatching' && (!inScope || inScope.has(baseline.careMapId)));
        const clientIds = new Set(clients.map(client => client.careMapId));
        const ordered = [...alerts]
            .filter(alert => clientIds.has(alert.careMapId))
            .sort((a, b) => (KIND_RANK[a.kind] - KIND_RANK[b.kind]) || b.observedOn.localeCompare(a.observedOn));

        const rows = new Map<string, { clientName: string; titles: string[]; newestOn: string; hasIncident: boolean }>();
        for (const alert of ordered) {
            const row = rows.get(alert.careMapId) ?? { clientName: alert.clientName, titles: [], newestOn: alert.observedOn, hasIncident: false };
            const title = stripFullStop(alert.title);
            if (!row.titles.includes(title)) {
                row.titles.push(title);
            }
            row.newestOn = row.newestOn > alert.observedOn ? row.newestOn : alert.observedOn;
            row.hasIncident = row.hasIncident || alert.kind === 'incident';
            rows.set(alert.careMapId, row);
        }

        const withFindings = [...rows.entries()]
            .map(([careMapId, row]) => ({
                careMapId,
                clientName: row.clientName,
                why: this.describeWhy(row.titles),
                newestOn: row.newestOn,
                hasIncident: row.hasIncident
            }))
            .sort((a, b) => b.newestOn.localeCompare(a.newestOn));

        if (!includeQuiet) {
            return withFindings;
        }
        const quiet = clients
            .filter(client => !rows.has(client.careMapId))
            .map(client => ({
                careMapId: client.careMapId,
                clientName: client.clientName,
                why: 'More raised about them recently than in their own earlier weeks',
                newestOn: '',
                hasIncident: client.hasRecentIncident
            }));
        return [...withFindings, ...quiet];
    };

    /**
     * Bea's line under the worklist: how many are worth watching and how many had something happen.
     * The list's own footer says how many it left out. A narrowed list is described as the selection
     * it is, since the rows then say nothing about the caseload as a whole, and the unreadable
     * caveat belongs to the whole.
     */
    describeWorthWatching = (params: { rows: IWorthWatchingRow[]; unreadableCount: number; narrowed: boolean }): string => {
        const { rows, unreadableCount, narrowed } = params;
        const unread = narrowed ? '' : describeUnreadable(unreadableCount, rows.length > 0);
        if (rows.length === 0) {
            return narrowed
                ? 'Nobody in this selection is worth watching.'
                : `Nobody I can read is worth watching right now: everyone is at or below their own baseline.${unread}`;
        }
        const scope = narrowed ? ' in this selection' : '';
        const lead = rows.length === 1
            ? `One client${scope} is worth watching`
            : `${rows.length} clients${scope} are worth watching`;
        return `${lead}${this.describeHappened(rows)}.${unread}`;
    };

    private describeHappened = (rows: IWorthWatchingRow[]): string => {
        const incidents = rows.filter(row => row.hasIncident).length;
        if (incidents === 0) {
            return '';
        }
        if (incidents === rows.length) {
            return rows.length === 1 ? ', and something already happened to them' : ', and something already happened to each of them';
        }
        return `, ${incidents === 1 ? 'one of whom' : `${incidents} of whom`} had something happen`;
    };

    // Each finding stays a sentence of its own, in its own casing: run together they would need
    // lowercasing, which would turn a name or a title at the start of one into "dorothy's daughter".
    private describeWhy = (titles: string[]): string => {
        const [first, ...rest] = titles;
        if (!first) {
            return '';
        }
        if (rest.length === 0) {
            return first;
        }
        if (rest.length === 1) {
            return `${first}. ${rest[0]}.`;
        }
        const unnamed = rest.length - (WHY_FINDINGS - 1);
        return `${first}. ${rest[0]}. ${unnamed} more ${unnamed === 1 ? 'thing' : 'things'} raised.`;
    };

    /**
     * Everyone the Needs Attention report leaves out, with how they are doing: their
     * standing against their own baseline, and the newest thing on their record, which on a steady
     * caseload is usually the good news.
     */
    restOfCaseload = (params: { baselines: IClientBaseline[]; alerts: IAgencyAlert[]; watchingIds: Set<string> }): IRestOfCaseloadRow[] => {
        const { baselines, alerts, watchingIds } = params;
        const newestFirst = [...alerts].sort((a, b) => b.observedOn.localeCompare(a.observedOn));

        return baselines
            .filter(baseline => !watchingIds.has(baseline.careMapId))
            .map(baseline => ({
                careMapId: baseline.careMapId,
                clientName: baseline.clientName,
                note: this.describeRest({ baseline, latest: newestFirst.find(alert => alert.careMapId === baseline.careMapId) })
            }));
    };

    private describeRest = (params: { baseline: IClientBaseline; latest?: IAgencyAlert }): string => {
        const { baseline, latest } = params;
        const recent = latest ? ` Most recent on file: ${stripFullStop(latest.title)}.` : '';

        if (baseline.standing === 'unknown') {
            return baseline.volume.recordedRecently
                ? `Not enough history yet to compare against their own baseline.${recent}`
                : 'Nothing recent on record to compare against their own baseline.';
        }
        if (baseline.standing === 'settled') {
            return `Recovered, with less raised about them recently than in their own earlier weeks and no incident recorded.${recent}`;
        }
        if (baseline.hasRecentIncident) {
            return `Something happened in the recent weeks.${recent}`;
        }
        if (baseline.standing === 'above') {
            return `More raised about them recently than in their own earlier weeks.${recent}`;
        }
        return `Steady against their own baseline.${recent}`;
    };
}
